import type {
  Campaign,
  CampaignProduct,
  ProductDefault,
  ProductSelectionGroup,
  ProductVariant,
} from '../types/campaign';
import type { Collab } from '../types/collab';


/* ------------------------------------------------------------------------- *
 * Campaign products.
 *
 * Kept in step by hand with foodfluence_dashboard/shared/src/utils/campaignProductUtils.ts, which
 * is the source of truth. The two differ only where the `Campaign` types differ: this repo has no
 * `productImageUrl`.
 *
 * `Campaign.productSelectionCatalog` holds the offer; `productName` / `productValue` /
 * `productImageUrl` are derived scalars written alongside. Campaigns created before products —
 * and those created by the custom-collab flow — carry only the scalars, so every reader goes
 * through `getProductSelectionCatalog` / `getCampaignProducts` rather than touching the
 * document directly.
 *
 * The brand-facing UI offers ONE selection group today. Every helper below is written so that a
 * single selection group reproduces the flat behaviour exactly — that equivalence is what lets
 * real selection groups ship later as a UI change rather than a migration.
 * ------------------------------------------------------------------------- */

/** Id given to the synthesised product row for a campaign that predates the product offer. */
export const LEGACY_CAMPAIGN_PRODUCT_ID = 'legacy-product';

/** Id given to the synthesised selection group wrapping that row. */
export const LEGACY_SELECTION_GROUP_ID = 'legacy-product-group';

/**
 * Id of the single selection group the flat UI writes. Stable, so an edit rewrites rather than
 * duplicates.
 */
export const DEFAULT_SELECTION_GROUP_ID = 'default-product-group';

/**
 * Round to cents. These figures are persisted and rendered raw in several places, and plain float
 * addition drifts (10.10 + 20.20 = 30.299999999999997).
 */
const roundToCents = (usd: number): number => Math.round(usd * 100) / 100;

const productValueOf = (product: ProductDefault): number => Number(product.productValue) || 0;

/**
 * What a product costs at each end of its own options.
 *
 * A product with no variants has one price, so both ends are its own value — which is why every
 * campaign written before variants behaves identically. A variant priced at nothing falls back to
 * the product's value rather than counting as free.
 */
export const getProductValueBounds = (
  product: ProductDefault
): { minUsd: number; maxUsd: number } => {
  const base = productValueOf(product);
  const priced = (product.variants ?? []).map((variant) =>
    variant.productValue == null ? base : Number(variant.productValue) || 0
  );
  if (!priced.length) return { minUsd: base, maxUsd: base };
  return { minUsd: Math.min(...priced), maxUsd: Math.max(...priced) };
};

/** What one product costs once its variant is chosen. */
export const getChosenProductValue = (
  product: ProductDefault,
  variantId: string | undefined
): number => {
  const base = productValueOf(product);
  if (!variantId) return base;
  const variant = (product.variants ?? []).find((v) => v.id === variantId);
  if (!variant || variant.productValue == null) return base;
  return Number(variant.productValue) || 0;
};

/** The variant a creator must choose between, if any. */
export const getProductVariants = (product: ProductDefault): ProductVariant[] =>
  product.variants ?? [];

/** Sum of a product selection, rounded to cents. */
export const sumProductValues = (products: ProductDefault[]): number =>
  roundToCents(products.reduce((total, product) => total + productValueOf(product), 0));

/** The fields of a campaign that describe its product offer. */
export type CampaignProductSource =
  | Pick<Campaign, 'productSelectionCatalog' | 'productName' | 'productValue'>
  | undefined
  | null;

/**
 * Every product on a campaign, newest shape first, falling back to the legacy scalars.
 *
 * A `products` array that is present but empty is authoritative, not a reason to fall back: the
 * wizard writes `[]` when a campaign leaves product compensation, while `productName` is sent as
 * undefined and therefore dropped by the merge update, leaving a stale scalar behind. Falling back
 * there would resurrect the removed product. Only a missing array is legacy.
 */
export const getProductSelectionCatalog = (
  campaign: CampaignProductSource
): ProductSelectionGroup[] => {
  if (!campaign) return [];
  if (campaign.productSelectionCatalog) return campaign.productSelectionCatalog;
  if (!campaign.productName) return [];
  // Campaigns that predate the product offer carry only the scalars. One group, one product, no
  // choice — which is exactly what those campaigns were.
  return [
    {
      id: LEGACY_SELECTION_GROUP_ID,
      name: campaign.productName,
      order: 0,
      pickLimit: 1,
      products: [
        {
          id: LEGACY_CAMPAIGN_PRODUCT_ID,
          name: campaign.productName,
          productValue: campaign.productValue ?? 0,
          // No `imageUrl`: this repo's Campaign carries no `productImageUrl` scalar.
        },
      ],
    },
  ];
};

/** Every product on a campaign, flattened across its groups. */
export const getCampaignProducts = (campaign: CampaignProductSource): CampaignProduct[] =>
  getProductSelectionCatalog(campaign).reduce<CampaignProduct[]>(
    (all, group) => all.concat(group.products ?? []),
    []
  );

/**
 * Wrap a flat product list as the single selection group the brand-facing UI writes.
 *
 * The wizard's state is still a flat list plus one allowance; this is the only place that shape
 * meets the stored one. When the UI learns to build several selection groups, this is what it
 * stops using.
 */
export const asSingleGroup = (
  products: CampaignProduct[],
  productPickLimit: number | undefined,
  name = 'Products'
): ProductSelectionGroup[] => {
  if (!products.length) return [];
  return [
    {
      id: DEFAULT_SELECTION_GROUP_ID,
      name,
      order: 0,
      pickLimit: getPickLimit(products, productPickLimit),
      products,
    },
  ];
};

/** The products every creator is sent, whatever they choose. */
export const getRequiredProducts = (products: CampaignProduct[]): CampaignProduct[] =>
  products.filter((product) => product.required);

/** The products a creator chooses between. */
export const getOptionalProducts = (products: CampaignProduct[]): CampaignProduct[] =>
  products.filter((product) => !product.required);

/**
 * How many OPTIONAL products a creator takes. Required products are always sent and are not
 * counted here. Never more than the campaign offers, never fewer than one where there is anything
 * optional at all, and defaulting to all of them when the campaign sets no limit.
 */
export const getPickLimit = (
  products: CampaignProduct[],
  productPickLimit: number | undefined
): number => {
  const optional = getOptionalProducts(products);
  if (!optional.length) return 0;
  const limit = Math.floor(productPickLimit ?? optional.length) || optional.length;
  return Math.max(1, Math.min(limit, optional.length));
};

/** How many optional products a creator takes from one selection group. */
export const getSelectionGroupPickLimit = (group: ProductSelectionGroup): number =>
  getPickLimit(group.products ?? [], group.pickLimit);

/** How many optional products a creator takes across the whole campaign. */
export const getCampaignPickLimit = (campaign: CampaignProductSource): number =>
  getProductSelectionCatalog(campaign).reduce(
    (total, group) => total + getSelectionGroupPickLimit(group),
    0
  );

/**
 * True when the campaign carries a real offer — a catalog with at least one product in it.
 *
 * Deliberately not `getCampaignProducts(...).length`: that synthesizes a product from a legacy
 * campaign's `productName`, which would make every pre-catalog campaign look like it had one. And
 * not `productSelectionCatalog?.length` either — a catalog holding one empty group offers nothing.
 *
 * This is what decides whether a collab goes through the selection wizard and whether its choice
 * is recorded, so those two must agree: a wizard that runs without a recording sends the creator
 * back to it forever.
 */
/**
 * The variants a creator must actually choose between.
 *
 * A single variant decides itself — asking which of one is a question with one answer — so it is
 * not a choice. Every "does this need a decision?" test goes through here, so the wizard's step
 * gate, the campaign's routing and the server's validation cannot disagree about what a variant
 * choice is. They did, and a product with exactly one variant fell in the gap.
 */
export const getVariantChoices = (product: ProductDefault): ProductVariant[] => {
  const variants = getProductVariants(product);
  return variants.length > 1 ? variants : [];
};

/**
 * The variant a pick resolves to: the creator's, or the only one on offer.
 *
 * The counterpart to {@link getVariantChoices} — what is never asked about must still be recorded,
 * or the pick ships and bills without naming which one.
 */
export const resolveVariantId = (
  product: ProductDefault,
  variantId?: string
): string | undefined => {
  if (variantId) return variantId;
  const variants = getProductVariants(product);
  return variants.length === 1 ? variants[0].id : undefined;
};

export const campaignHasProductCatalog = (campaign: CampaignProductSource): boolean =>
  (campaign?.productSelectionCatalog ?? []).some((group) => (group.products ?? []).length > 0);

/**
 * True when the creator has a decision to make in at least one selection group.
 *
 * Two kinds of decision count. Either the group offers more optional products than the allowance
 * takes, or some product it offers comes in more than one variant — a creator who receives every
 * product still has to say which size, and a campaign that asks that is not a fixed offer.
 */
export const campaignOffersProductChoice = (campaign: CampaignProductSource): boolean =>
  getProductSelectionCatalog(campaign).some((group) => {
    const products = group.products ?? [];
    if (getOptionalProducts(products).length > getSelectionGroupPickLimit(group)) return true;
    return products.some((product) => getVariantChoices(product).length > 0);
  });

export interface CampaignValueRange {
  /** Cheapest valid selection: the least valuable products the limit allows. */
  minUsd: number;
  /** Priciest selection the limit permits — the brand's max spend per collab. */
  maxUsd: number;
}

/** Sum of each product at the given end of its own variant range. */
const sumBounds = (products: CampaignProduct[], end: 'minUsd' | 'maxUsd'): number =>
  products.reduce((total, product) => total + getProductValueBounds(product)[end], 0);

/**
 * The value range a campaign advertises. Derived at read time rather than stored, so it cannot
 * drift from the offer it summarises.
 *
 * Per selection group: a creator takes exactly that group's allowance of its optional PRODUCTS —
 * variants are options within a product, not extra items — so the floor is the cheapest that many
 * and the ceiling the priciest. Required products sit in BOTH ends; every creator gets them.
 *
 * A product that comes in variants is worth its cheapest variant at the floor and its priciest at
 * the ceiling, so the two ends rank products differently. A product with no variants has one
 * price at both ends, which is why campaigns written before variants are unaffected.
 */
export const getCampaignValueRange = (groups: ProductSelectionGroup[]): CampaignValueRange => {
  let minUsd = 0;
  let maxUsd = 0;
  for (const group of groups) {
    const products = group.products ?? [];
    minUsd += sumBounds(getRequiredProducts(products), 'minUsd');
    maxUsd += sumBounds(getRequiredProducts(products), 'maxUsd');
    const limit = getSelectionGroupPickLimit(group);
    if (!limit) continue;
    const optional = getOptionalProducts(products);
    // Ranked separately at each end: the cheapest N by their floor is not always the priciest N by
    // their ceiling once variants spread a product's price. Copies, so the caller's array order
    // does not depend on whether this ran.
    const byFloor = [...optional].sort(
      (a, b) => getProductValueBounds(a).minUsd - getProductValueBounds(b).minUsd
    );
    const byCeiling = [...optional].sort(
      (a, b) => getProductValueBounds(b).maxUsd - getProductValueBounds(a).maxUsd
    );
    minUsd += sumBounds(byFloor.slice(0, limit), 'minUsd');
    maxUsd += sumBounds(byCeiling.slice(0, limit), 'maxUsd');
  }
  return { minUsd: roundToCents(minUsd), maxUsd: roundToCents(maxUsd) };
};

/**
 * The scalars written alongside `productSelectionCatalog` on every save, for readers not yet
 * migrated — step-3 validation, the backend validation guard, and the brand-managed
 * compensation-mode check that treats any positive product value as product mode.
 *
 * `productValue` is the range CEILING: writing the floor would let a campaign whose products are
 * mostly cheap read as worth less than the brand may actually be charged.
 */
export const getCampaignProductScalars = (
  groups: ProductSelectionGroup[]
): { productName?: string; productValue: number } => {
  const first = groups[0]?.products?.[0];
  return {
    productName: first?.name,
    productValue: getCampaignValueRange(groups).maxUsd,
  };
};

export type ProductSelectionValidation = { ok: true } | { ok: false; message: string };

/**
 * One pick: a product, plus which of its variants the creator wants.
 *
 * `variantId` is required exactly when the product has variants. It is never a way to take the
 * same product twice — the allowance counts products.
 */
export interface ProductSelectionChoice {
  productId: string;
  variantId?: string;
}

/**
 * Whether a creator's choice is legal for a campaign: exactly each selection group's allowance of
 * that group's optional products, nothing offered twice, nothing from outside the campaign.
 *
 * Required products may be echoed back — they are sent regardless — but never count towards an
 * allowance and never stand in for an optional pick.
 *
 * A product that comes in variants must name one, and it must be a variant that product actually
 * offers. A product with no variants must not name one.
 *
 * Lives in the shared package so the creator app and the server apply identical rules — the server
 * validates independently of the client.
 */
export const validateProductSelection = (
  groups: ProductSelectionGroup[],
  selections: ProductSelectionChoice[]
): ProductSelectionValidation => {
  const chosen = selections ?? [];
  const chosenIds = chosen.map((choice) => choice.productId);
  // No early return for an empty campaign: with nothing offered, the checks below already accept
  // an empty selection and reject a non-empty one. Short-circuiting here waved through any id at
  // all, so a campaign offering nothing accepted a product it had never heard of.
  if (new Set(chosenIds).size !== chosenIds.length) {
    return { ok: false, message: 'The same product was chosen more than once.' };
  }

  // Unknown ids first, so a selection naming a product the campaign never offered says so rather
  // than reporting a count that happens to be wrong as a side effect.
  const offeredIds = new Set(
    groups.flatMap((group) => (group.products ?? []).map((product) => product.id))
  );
  if (chosenIds.some((id) => !offeredIds.has(id))) {
    return { ok: false, message: 'A chosen product is not offered on this campaign.' };
  }

  // Each pick must resolve to exactly one variant where the product has them. Checked before the
  // per-group counts so a missing size is reported as a missing size, not as a wrong total.
  const offeredById = new Map(
    groups.flatMap((group) => (group.products ?? []).map((product) => [product.id, product]))
  );
  //
  // Required products are checked too, not only the ones the request names. A required product is
  // sent whether or not the creator mentions it, so omitting one used to pass validation and then
  // snapshot at base price with no variant — the wrong charge, and an order nobody can fulfil.
  const requiredProducts = groups.flatMap((group) => getRequiredProducts(group.products ?? []));
  const needsCheck = [
    ...chosen,
    ...requiredProducts
      .filter((product) => !chosen.some((choice) => choice.productId === product.id))
      .map((product) => ({ productId: product.id, variantId: undefined })),
  ];
  for (const choice of needsCheck) {
    const product = offeredById.get(choice.productId);
    if (!product) continue;
    const variants = getVariantChoices(product);
    if (!variants.length) {
      // A product with nothing to choose between must not name a choice: the id would be silently
      // dropped on snapshot, so the request and what gets shipped would disagree.
      if (choice.variantId && !getProductVariants(product).some((v) => v.id === choice.variantId)) {
        return {
          ok: false,
          message: `That option is not available for ${product.name}.`,
        };
      }
      continue;
    }
    if (!choice.variantId) {
      return { ok: false, message: `Choose an option for ${product.name}.` };
    }
    if (!variants.some((variant) => variant.id === choice.variantId)) {
      return {
        ok: false,
        message: `That option is not available for ${product.name}.`,
      };
    }
  }

  for (const group of groups) {
    const products = group.products ?? [];
    const optionalIds = new Set(getOptionalProducts(products).map((product) => product.id));
    const chosenOptional = chosenIds.filter((id) => optionalIds.has(id));
    const limit = getSelectionGroupPickLimit(group);
    if (chosenOptional.length !== limit) {
      // Named only when there is more than one selection group; with the single group the UI
      // writes today the group's name would be noise in the creator's error message.
      const where = groups.length > 1 ? ` from ${group.name}` : '';
      return {
        ok: false,
        message: `Choose ${limit} product${limit === 1 ? '' : 's'}${where}.`,
      };
    }
  }
  return { ok: true };
};

/**
 * The product value of one collab — what this creator actually got.
 *
 * The single entry point for every money and reporting read. Falls back to the campaign's estimate
 * when the creator made no selection, so collabs accepted before this feature bill exactly as they
 * did before.
 */
export const resolveCollabProductValue = (
  collab: Pick<Collab, 'selectedProducts' | 'productValue'> | undefined | null,
  campaign: CampaignProductSource
): number => {
  if (collab?.selectedProducts?.length) return sumProductValues(collab.selectedProducts);
  // The invite-time snapshot, before the campaign's live figure. Without this step a campaign
  // edited after a collab was accepted would retroactively change what that collab is worth —
  // the very bug the snapshots exist to prevent, reintroduced through the fallback.
  //
  // Truthy, NOT `typeof === 'number'`. The pre-catalog invite path wrote `campaign.productValue ?? 0`,
  // so a stored 0 means the campaign had no figure of its own at invite time — not that the collab
  // is worth nothing. Every money path used to read the campaign directly and never saw these, and
  // prod holds ~5.7k of them. Short-circuiting on 0 would drop the product component from their
  // billing entirely. A campaign genuinely worth zero still resolves zero, because the fallback
  // below is zero too.
  if (collab?.productValue) return collab.productValue;
  return campaign?.productValue ?? 0;
};

/**
 * A campaign or collab's products as one short line — `Widget A +2 more`. For rows and pills that
 * cannot wrap; surfaces with room list the products in full.
 */
export const getProductSummaryLabel = (
  products: (ProductDefault & { variantName?: string })[]
): string => {
  if (!products.length) return '';
  const [first, ...rest] = products;
  // The variant is what distinguishes one pick from another where a brand offers the same shirt
  // in three colours — naming the product alone would make them read identically.
  const name = first.variantName ? `${first.name} (${first.variantName})` : first.name;
  return rest.length ? `${name} +${rest.length} more` : name;
};
